import { FC } from 'react';
import { createUseStyles } from 'react-jss';
import dayjs from 'dayjs';
import { IonButton, IonButtons, IonItem, IonLabel } from '@ionic/react';
import { useInstance } from '@meraki-internal/react-dependency-injection';
import { useSubscription } from '@meraki-internal/state';
import { Card } from '../components/Card';
import { Text } from '../components/Text';
import { CareMapState } from '../caremap/CareMapState';
import { HistoryViewModel } from '../support/HistoryViewModel';
import { QueryParamDrivenDrawerHelper } from '../components/query-param-driver-drawer/QueryParamDrivenDrawerHelper';
import { CaremapInsightsModelFactory } from './CaremapInsightsModelFactory';
import { InsightsModel } from './InsightsModel';
import { IInsight } from './IInsight';
import { insightTypeMeta } from './insightTypeMeta';
import { useInsightEnterAnimation } from './useInsightEnterAnimation';

// How many recent insights to tease on home. The full list (incl. low-confidence) lives on the page.
const HOME_COUNT = 2;

const formatDate = (date: string) => dayjs(date).format('MM/DD/YY');

const useStyles = createUseStyles({
    // The home cards sit 20px apart and ion-card only contributes 10px of that. flow-root keeps the
    // card's own margins inside this wrapper, so the extra 10px exists only when the section renders.
    section: {
        display: 'flow-root',
        marginBottom: 10,
    },
    header: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        // The View All button is 44px tall and centres against the avatar and title, so it, not the
        // padding, sets where the header ink lands: 10px here puts the avatar 16px below the card's
        // top edge, matching the other home cards. The rows below carry their own leading, so the
        // header needs no bottom margin for the title-to-first-row gap to match them.
        paddingTop: 10,
    },
    headerLeft: {
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        paddingLeft: 16,
    },
    avatar: {
        width: 32,
        height: 32,
        borderRadius: '50%',
        objectFit: 'cover' as const,
    },
    eyebrow: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 8,
    },
    // tinted type pill: icon + small-caps label in the type color, on a 10%-opacity wash of it
    type: {
        display: 'inline-flex',
        alignItems: 'center',
        gap: 5,
        fontSize: 12,
        fontWeight: 600,
        textTransform: 'uppercase' as const,
        letterSpacing: '0.04em',
        borderRadius: 999,
        padding: '3px 9px',
    },
    date: {
        fontSize: 12,
        color: 'var(--ion-color-step-600)',
        flexShrink: 0,
    },
    title: {
        // Match the Recent Summaries title weight (CareMapNotesListTitle uses 500) so the two
        // home sections read as one system.
        fontWeight: 500,
        // .sc-ion-label-md-s h2 sets a smaller font-size than we want
        fontSize: 'inherit !important',
        // the title is the row's last line, so its default bottom margin would leave the row bottom-heavy
        marginBottom: 0,
    },
});

const InsightHomeRow: FC<{ insight: IInsight; isLast: boolean; onOpen: (insight: IInsight) => void }> = ({ insight, isLast, onOpen }) => {
    const classes = useStyles();
    const { label, Icon, color } = insightTypeMeta(insight.type);
    return (
        <IonItem
            button
            detail
            lines={isLast ? 'none' : undefined}
            onClick={() => onOpen(insight)}
            data-type="insight-home-card"
            data-insight-id={insight.insightId}
            style={{'--border-radius': '8px'}}
        >
            <IonLabel className="ion-text-wrap">
                <div className={classes.eyebrow}>
                    <span className={classes.type} style={{ color, backgroundColor: `${color}1A` }}>
                        <Icon color="inherit" size="small" />
                        {label}
                    </span>
                    <span className={classes.date}>{formatDate(insight.dateRangeFrom)} – {formatDate(insight.dateRangeTo)}</span>
                </div>
                <h2 className={`${classes.title} mp-mask`}>{insight.title}</h2>
            </IonLabel>
        </IonItem>
    );
};

// The home insights teaser: a section shaped like Recent Summaries (header + View All + a couple of
// recents), each card fully clickable into its detail drawer. Replaces the old one-line strip. Shows
// only medium/high insights (getActiveProactive) -- low-confidence is pull-only, on the page.
export const InsightsHomeSection: FC = () => {
    const classes = useStyles();

    const factory = useInstance(CaremapInsightsModelFactory);
    useSubscription(() => factory);

    const careMapState = useInstance(CareMapState);
    const history = useInstance(HistoryViewModel);
    const drawerHelper = useInstance(QueryParamDrivenDrawerHelper);

    let model: InsightsModel | undefined;
    try {
        model = factory.forActiveCaremap();
    } catch {
        // no active caremap yet -- render nothing rather than crash the home page
    }

    const insights = model ? model.getActiveProactive().slice(0, HOME_COUNT) : [];
    const enterClassName = useInsightEnterAnimation(insights.map(insight => insight.insightId));

    if (!model || insights.length === 0) {
        return null;
    }

    const onViewAll = () => {
        const careMapId = careMapState.getActiveCaremap()?.careMapId;
        if (careMapId) {
            history.push(`/insights/${careMapId}`);
        }
    };

    const onOpen = (insight: IInsight) => {
        drawerHelper.open({ mode: 'view-insight', insightId: insight.insightId, surface: 'home' });
    };

    return (
        <div className={classes.section}>
            <Card data-type="insights-home-section">
                <div className={classes.header} data-id="insights-section">
                    <div className={classes.headerLeft}>
                        <img src="/assets/images/help/helper-bea.png" alt="Bea" className={classes.avatar} />
                        <Text><b>Bea Noticed</b></Text>
                    </div>
                    <IonButtons slot="end">
                        <div style={{ marginRight: 12 }}>
                            <IonButton fill="clear" color="primary" data-id="view-all-insights" onClick={onViewAll}>
                                View All
                            </IonButton>
                        </div>
                    </IonButtons>
                </div>

                {insights.map((insight, idx) => (
                    <div key={insight.insightId} className={enterClassName(insight.insightId)}>
                        <InsightHomeRow insight={insight} isLast={idx === insights.length - 1} onOpen={onOpen} />
                    </div>
                ))}
            </Card>
        </div>
    );
};
